import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SelectField } from '../../components/ui/SelectField';
import {
  inheritedTemplateLabel,
  resolveReportTemplateForAccount,
  type TemplateAccount,
} from '../../lib/portalReport/reportTemplate';
import {
  REPORT_TEMPLATES,
  REPORT_TEMPLATE_LABELS,
  type ReportTemplate,
} from '../../types/reportTemplate';
import type { Account } from '../../types/account';
import { getReportTemplateConfig } from './api';

// The account page's "Client report template" control (2026-11-11, round 3).
//
// ⚠️ ABSENT IS NOT STANDARD. The empty option is "Use inherited" and clears an
// override; it is a different statement from choosing Standard, which stops the
// walk and overrides a group or marque. The label under the select says which
// level the inherited value comes from (own / group / marque / default).
//
// ⚠️ THE CLIENT RESOLUTION HERE IS FOR THE LABEL ONLY. What a client actually sees
// is resolved server-side (reportService.reportContext) from the same pure
// function; this component mirrors the server's brand walk only to feed the legacy
// step, and a drift between the two would mis-LABEL, never mis-render.

export interface ReportTemplateFieldProps {
  /** The stored value on this account, or '' for inherit. */
  value: ReportTemplate | '';
  onChange: (next: ReportTemplate | '') => void;
  /** This account as it stands in the form (its id may be absent on a create). */
  draft: { id: string | null; parentAccountId: string | null; brandAccountId: string | null; industry: string };
  roster: readonly Account[];
}

/**
 * The brand ancestor of an account, mirroring the server's `computeBrandAncestorMap`
 * for the LEGACY step only: a marque pointer that resolves to a brand-type account
 * wins, else the nearest brand-type ancestor on the parent chain.
 */
function clientBrandOf(
  account: TemplateAccount & { accountType?: string },
  byId: ReadonlyMap<string, Account>,
): string | null {
  const marque = account.brandAccountId ? byId.get(account.brandAccountId) : undefined;
  if (marque && marque.accountType === 'brand') return marque.id;
  const seen = new Set<string>();
  let cursor = account.parentAccountId ?? null;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const parent = byId.get(cursor);
    if (!parent) return null;
    if (parent.accountType === 'brand') return parent.id;
    cursor = parent.parentAccountId ?? null;
  }
  return null;
}

export function ReportTemplateField({ value, onChange, draft, roster }: ReportTemplateFieldProps) {
  const config = useQuery({ queryKey: ['report-template-config'], queryFn: getReportTemplateConfig });
  const inherited = useMemo(() => {
    const byId = new Map(roster.map((a) => [a.id, a]));
    const accounts = new Map<string, TemplateAccount>(
      roster.map((a) => [
        a.id,
        {
          id: a.id,
          parentAccountId: a.parentAccountId ?? null,
          brandAccountId: a.brandAccountId ?? null,
          reportTemplate: a.reportTemplate ?? null,
          industry: a.industry,
        },
      ]),
    );
    const me: TemplateAccount = {
      id: draft.id ?? '__new__',
      parentAccountId: draft.parentAccountId,
      brandAccountId: draft.brandAccountId,
      // ⚠️ Deliberately NULL: the label answers "what do I get if I choose nothing".
      reportTemplate: null,
      industry: draft.industry,
    };
    const brand = clientBrandOf(me, byId);
    const resolution = resolveReportTemplateForAccount(me, {
      accounts,
      brandByAccount: new Map([[me.id, brand]]),
      brandHasLegacyGoalSplit: (b) => byId.get(b)?.portalMetaGoalReport === true,
      automotiveDefaultOn: config.data?.automotiveDefaultOn ?? false,
    });
    return inheritedTemplateLabel(resolution, (id) => byId.get(id)?.name ?? null);
  }, [roster, draft.id, draft.parentAccountId, draft.brandAccountId, draft.industry, config.data]);

  return (
    <div className="border-t border-slate-200 pt-4">
      <SelectField
        aria-label="Client report template"
        value={value}
        onValueChange={(v) => onChange(v as ReportTemplate | '')}
        options={[
          { value: '', label: 'Use inherited' },
          ...REPORT_TEMPLATES.map((t) => ({ value: t, label: REPORT_TEMPLATE_LABELS[t] })),
        ]}
      />
      <p className="mt-1 text-xs text-slate-500">
        Client report template. Inherited: {inherited}.
        {value === ''
          ? ''
          : ` This account is set to ${REPORT_TEMPLATE_LABELS[value]}, which overrides the inherited value.`}
      </p>
    </div>
  );
}
